import React, { useState, useRef, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
} from "components/StyledComponents/styledComponents.js";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import { CustomPaper, autoSelectStyle } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { writeFile, utils } from "xlsx";

const resolveDsaId = (dsaObj) => {
  if (!dsaObj) return undefined;
  const candidates = ["dsa_Id", "dsaId", "dsa_id", "DSA_Id", "DSAID", "id", "ID"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const idKey = Object.keys(dsaObj).find(
    (k) => k.toLowerCase().includes("id") && !isNaN(dsaObj[k])
  );
  return idKey ? dsaObj[idKey] : undefined;
};

const resolveDsaName = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaName", "dsa_Name", "dsa_name", "DSAName", "name", "Name"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const nameKey = Object.keys(dsaObj).find((k) => k.toLowerCase().includes("name"));
  return nameKey ? dsaObj[nameKey] : "";
};

const resolveDsaStatus = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaStatus", "dsa_Status", "dsa_status", "DSAStatus", "status", "Status"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null) return dsaObj[key];
  }
  return "";
};

export default function IncompleteDisbursements() {
  const [controller] = useMaterialUIController();
  const jwt_token = Cookie.get("jwt_token");
  const MInput = inputStyle();

  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [dsaList, setDsaList] = useState([]);
  const [selectedDsa, setSelectedDsa] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [pinnedFooter, setPinnedFooter] = useState([]);

  const gridRef = useRef(null);

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };

  const computeFooter = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setPinnedFooter([]);
      return;
    }
    const sum = (field) => rows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    setPinnedFooter([
      {
        Date: "",
        CustomerName: "Total",
        ApplicationNumber: "",
        Remarks: "",
        SanctionAmount: sum("SanctionAmount"),
        TotalDisbursedAmount: sum("TotalDisbursedAmount"),
        PendingDisburseAmount: sum("PendingDisburseAmount"),
      },
    ]);
  }, []);

  const onFilterChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const visibleRows = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeFooter(visibleRows);
  }, [computeFooter]);

  const fetchDsaList = async () => {
    try {
      const res = await fetch(URLs.dsaMasterGetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch DSA list");
      const data = await res.json();
      const rawList = Array.isArray(data) ? data : data.data || [];
      setDsaList(rawList.filter((item) => resolveDsaStatus(item) === "S"));
    } catch {
      showAlert("Failed to fetch DSA list", "error");
    }
  };

  useEffect(() => {
    fetchDsaList();
  }, []);

  const fetchData = async () => {
    if (!startDate || !endDate) {
      showAlert("Please select both start and end dates", "error");
      return;
    }
    if (!selectedDsa) {
      showAlert("Please select a DSA", "error");
      return;
    }
    const dsaId = resolveDsaId(selectedDsa);
    if (dsaId === undefined || dsaId === null || dsaId === "") {
      showAlert("Selected DSA has no valid ID. Please contact support.", "error");
      return;
    }

    setLoading(true);
    try {
      const formattedStart = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEnd = dayjs(endDate).format("YYYY-MM-DD");
      const url = `${URLs.pendingDisbursementsUrl}startDate=${formattedStart}&endDate=${formattedEnd}&dsaId=${dsaId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (response.status === 204) {
        setRowData([]);
        setPinnedFooter([]);
        showAlert("No data found for the selected criteria", "info");
        return;
      }
      const data = await response.json();
      if (response.ok) {
        setRowData(data);
        computeFooter(data);
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (error) {
      showAlert(`Failed to fetch data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const applyAutoWidth = (ws, sheetRows, headers) => {
    const colWidths = headers.map((h) => {
      const maxDataLen = sheetRows.reduce((max, row) => {
        const val = row[h] !== undefined && row[h] !== null ? String(row[h]) : "";
        return Math.max(max, val.length);
      }, 0);
      return { wch: Math.min(60, Math.max(10, h.length, maxDataLen)) };
    });
    ws["!cols"] = colWidths;
  };

  const handleDownload = () => {
    if (!gridRef.current?.api) return;
    const exportRows = [];
    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) exportRows.push(node.data);
    });
    if (!exportRows.length) return;

    const sum = (field) => exportRows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    const totalsRow = {
      CustomerName: "Total",
      ApplicationNumber: "",
      SanctionAmount: sum("SanctionAmount"),
      TotalDisbursedAmount: sum("TotalDisbursedAmount"),
      PendingDisburseAmount: sum("PendingDisburseAmount"),
    };

    const headers = [
      "Date",
      "Customer Name",
      "Application Number",
      "Remarks",
      "Applied Amount",
      "Total Disbursed Amount",
      "Pending Disburse Amount",
    ];
    const sheetRows = [...exportRows, totalsRow].map((row) => ({
      Date: row.Date ? dayjs(row.Date).format("DD-MMM-YYYY") : "",
      "Customer Name": row.CustomerName ?? "",
      "Application Number": row.ApplicationNumber ?? "",
      Remarks: row.Remarks ?? "",
      "Applied Amount": row.SanctionAmount ?? 0,
      "Total Disbursed Amount": row.TotalDisbursedAmount ?? 0,
      "Pending Disburse Amount": row.PendingDisburseAmount ?? 0,
    }));

    const ws = utils.json_to_sheet(sheetRows, { header: headers });
    applyAutoWidth(ws, sheetRows, headers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Incomplete Disbursements");
    writeFile(wb, "Incomplete_Disbursements.xlsx");
  };

  const columnDefs = [
    {
      headerName: "Customer Name",
      field: "CustomerName",
      filter: true,
      sortable: true,
      minWidth: 220,
      flex: 2,
    },

    {
      headerName: "Application Number",
      field: "ApplicationNumber",
      filter: true,
      sortable: true,
      minWidth: 220,
      flex: 2,
    },

    {
      headerName: "Sanction Amount",
      field: "SanctionAmount",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Total Disbursed Amount",
      field: "TotalDisbursedAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Pending Disburse Amount",
      field: "PendingDisburseAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },

    {
      headerName: "Date",
      field: "Date",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
      valueFormatter: (p) => {
        if (!p.value) return "";
        return dayjs(p.value).format("DD-MMM-YYYY");
      },
    },
    {
      headerName: "Remarks",
      field: "Remarks",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
  ];

  return (
    <>
      {/* Loading dialog */}
      <Dialog open={loading} onClose={() => {}}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={() => setOpen(false)} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {/* Main table */}
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Grid container spacing={2} alignItems="center" wrap="wrap" sx={{ mb: 1 }}>
            {/* Start Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* Select DSA */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete
                options={dsaList}
                getOptionLabel={(option) => resolveDsaName(option)}
                value={selectedDsa}
                onChange={(_, v) => setSelectedDsa(v || null)}
                filterOptions={(options, { inputValue }) => {
                  const input = inputValue.toLowerCase();
                  return options
                    .sort((a, b) => {
                      const aName = resolveDsaName(a).toLowerCase();
                      const bName = resolveDsaName(b).toLowerCase();
                      const aS = aName.startsWith(input);
                      const bS = bName.startsWith(input);
                      return aS === bS ? 0 : aS ? -1 : 1;
                    })
                    .filter((o) => resolveDsaName(o).toLowerCase().includes(input));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select DSA"
                    variant="outlined"
                    fullWidth
                    required
                    sx={{ height: 43, ...autoSelectStyle, ...MInput.b }}
                    InputLabelProps={{ sx: { color: MInput.a } }}
                  />
                )}
                PaperComponent={CustomPaper}
              />
            </Grid>

            {/* Get button */}
            <Grid item xs={12} sm={6} md={1.5}>
              <StyledButton onClick={fetchData} fullWidth>
                Get
              </StyledButton>
            </Grid>

            {/* Excel download */}
            <Grid item xs="auto">
              <StyledButton onClick={handleDownload}>
                <DownloadForOfflineIcon fontSize="medium" />
              </StyledButton>
            </Grid>
          </Grid>
        </Box>

        <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={pinnedFooter}
            getRowStyle={getRowStyle}
            onFilterChanged={onFilterChanged}
            animateRows={true}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </>
  );
}
